/**
 * PATH: apps/hr/src/pages/employees/EmployeesPage.tsx
 * PURPOSE: Employee directory with search, filters, stats, and table — orchestrator
 * WHY: Main employee listing page, split from 1172-line monolith
 *
 * DESIGN: Matches Workforce Hub PeoplePage patterns:
 * - Stats cards row
 * - Search and filters
 * - Paginated table with hover states
 * - Add employee modal
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Search, Plus, Download, ChevronLeft, ChevronRight,
  UserCheck, UserMinus, CalendarOff, X, AlertCircle, RefreshCw,
} from 'lucide-react'

import { cn } from '../../lib/utils'
import { hrApi } from '../../lib/setupApi'
import type { Employee, EmployeeFilters, EmploymentType, EmployeeStatus } from '../../types'

import { type WorkerApiResponse, mapWorkerToEmployee } from './employees-list/api-mapping'
import { StatCard } from './employees-list/StatCard'
import { EmployeeRow } from './employees-list/EmployeeRow'
import { AddEmployeeModal } from './employees-list/AddEmployeeModal'
import { InitialPasswordModal, ErrorModal } from './employees-list/EmployeeModals'

export default function EmployeesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | ''>('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState<EmploymentType | ''>('')
  const [page, setPage] = useState(1)
  const [showAddModal, setShowAddModal] = useState(false)
  const pageSize = 10

  const [passwordModal, setPasswordModal] = useState<{ isOpen: boolean; employeeName: string; email: string; initialPassword: string }>({ isOpen: false, employeeName: '', email: '', initialPassword: '' })
  const [enablingLoginFor, setEnablingLoginFor] = useState<string | null>(null)
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' })

  const { data: apiData, isLoading: apiLoading, error: apiError, refetch } = useQuery({
    queryKey: ['hr-employees', search],
    queryFn: async () => {
      try {
        const response = await hrApi.getEmployees(search ? { search } : undefined)
        return response.data as { workers: WorkerApiResponse[]; total: number }
      } catch (err) {
        console.error('[HR] API error:', err)
        throw err
      }
    },
    staleTime: 30_000,
    retry: 1,
  })

  const enableLoginMutation = useMutation({
    mutationFn: (workerId: string) => hrApi.enableWorkerLogin(workerId),
    onSuccess: (response, workerId) => {
      const data = response.data
      const employee = allEmployees.find(e => e.id === workerId)
      const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Employee'
      setPasswordModal({ isOpen: true, employeeName, email: data.email, initialPassword: data.initial_password })
      queryClient.invalidateQueries({ queryKey: ['hr-employees'] })
      setEnablingLoginFor(null)
    },
    // The shared @quotech/api client rejects with ApiError ({ message,
    // status, details }) whose `message` already carries the server's
    // `detail`; keep the axios `response` read as a legacy fallback.
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      console.error('[HR] Enable login failed:', error)
      setErrorModal({ isOpen: true, title: 'Enable Login Failed', message: error.response?.data?.detail || error.message || 'Failed to enable login. Please try again.' })
      setEnablingLoginFor(null)
    },
  })

  const handleEnableLogin = (employee: Employee) => {
    if (!employee.email) { setErrorModal({ isOpen: true, title: 'Email Required', message: 'Employee must have an email address to enable login.' }); return }
    if (employee.user_id) { setErrorModal({ isOpen: true, title: 'Already Enabled', message: 'Login is already enabled for this employee.' }); return }
    setEnablingLoginFor(employee.id)
    enableLoginMutation.mutate(employee.id)
  }

  const allEmployees = useMemo(() => {
    if (apiData?.workers && Array.isArray(apiData.workers)) return apiData.workers.map(mapWorkerToEmployee)
    return []
  }, [apiData])

  const departmentOptions = useMemo(() => Array.from(new Set(allEmployees.map(e => e.department).filter(Boolean))).sort(), [allEmployees])
  const locationOptions = useMemo(() => Array.from(new Set(allEmployees.map(e => e.location).filter(Boolean))).sort(), [allEmployees])

  const stats = useMemo(() => ({
    total: allEmployees.length,
    active: allEmployees.filter(e => e.status === 'active').length,
    onLeave: allEmployees.filter(e => e.status === 'on_leave').length,
    inactive: allEmployees.filter(e => e.status === 'inactive' || e.status === 'terminated').length,
  }), [allEmployees])

  const filters: EmployeeFilters = useMemo(() => ({ search: search || undefined, status: statusFilter || undefined, department: departmentFilter || undefined, employmentType: typeFilter || undefined }), [search, statusFilter, departmentFilter, typeFilter])

  const { employees, total, totalPages } = useMemo(() => {
    let filtered = [...allEmployees]
    if (filters.status) filtered = filtered.filter(e => e.status === filters.status)
    if (filters.department) filtered = filtered.filter(e => e.department === filters.department)
    if (filters.employmentType) filtered = filtered.filter(e => e.employmentType === filters.employmentType)
    const t = filtered.length
    const tp = Math.ceil(t / pageSize)
    const start = (page - 1) * pageSize
    return { employees: filtered.slice(start, start + pageSize), total: t, totalPages: tp }
  }, [allEmployees, filters, page, pageSize])

  const handleStatusFilter = (status: EmployeeStatus | '') => { setStatusFilter(status === statusFilter ? '' : status); setPage(1) }
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setDepartmentFilter(''); setTypeFilter(''); setPage(1) }
  const hasFilters = search || statusFilter || departmentFilter || typeFilter

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Employees</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Manage your team and employee information</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} disabled={apiLoading} className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50" title="Refresh from API"><RefreshCw className={cn('w-4 h-4', apiLoading && 'animate-spin')} /></button>
          <button className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Download className="w-4 h-4" /><span className="hidden sm:inline">Export</span></button>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Employee</span></button>
        </div>
      </div>

      {apiError && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div className="flex-1"><p className="text-sm font-medium text-amber-800 dark:text-amber-200">Could not load employees from server</p><p className="text-xs text-amber-600 dark:text-amber-400">The list stays empty until the server responds.</p></div>
          <button onClick={() => refetch()} className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-800/40 rounded-lg transition-colors">Retry</button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Employees" value={stats.total} icon={Users} color="teal" />
        <StatCard title="Active" value={stats.active} icon={UserCheck} color="emerald" onClick={() => handleStatusFilter('active')} isActive={statusFilter === 'active'} />
        <StatCard title="On Leave" value={stats.onLeave} icon={CalendarOff} color="blue" onClick={() => handleStatusFilter('on_leave')} isActive={statusFilter === 'on_leave'} />
        <StatCard title="Inactive" value={stats.inactive} icon={UserMinus} color="red" onClick={() => handleStatusFilter('inactive')} isActive={statusFilter === 'inactive'} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search by name, email, department..." className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
        </div>
        <select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1) }} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent">
          <option value="">All Departments</option>
          {departmentOptions.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as EmploymentType | ''); setPage(1) }} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent">
          <option value="">All Types</option>
          <option value="full_time">Full Time</option>
          <option value="part_time">Part Time</option>
          <option value="casual">Casual</option>
          <option value="contractor">Contractor</option>
        </select>
        {hasFilters && <button onClick={clearFilters} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"><X className="w-4 h-4" />Clear</button>}
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden lg:table-cell">Position</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden xl:table-cell">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden lg:table-cell">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden xl:table-cell">Hire Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length > 0 ? employees.map((employee) => (
                <EmployeeRow key={employee.id} employee={employee} onClick={() => navigate(`/employees/${employee.id}`)} onEnableLogin={handleEnableLogin} isEnablingLogin={enablingLoginFor === employee.id} />
              )) : (
                <tr><td colSpan={8} className="px-4 py-12 text-center"><Users className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" /><p className="text-slate-500 dark:text-slate-400">No employees found</p>{hasFilters && <button onClick={clearFilters} className="mt-2 text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400">Clear filters</button>}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-400">Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} employees</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} className={cn('min-w-[32px] h-8 text-sm rounded-lg transition-colors', p === page ? 'bg-teal-600 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400')}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      <AddEmployeeModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} departmentOptions={departmentOptions} locationOptions={locationOptions} />
      <InitialPasswordModal isOpen={passwordModal.isOpen} onClose={() => setPasswordModal({ isOpen: false, employeeName: '', email: '', initialPassword: '' })} employeeName={passwordModal.employeeName} email={passwordModal.email} initialPassword={passwordModal.initialPassword} />
      <ErrorModal isOpen={errorModal.isOpen} onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })} title={errorModal.title} message={errorModal.message} />
    </div>
  )
}
